import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import SplitPane from './SplitPane';
import MonthView from './MonthView';
import WeekView2 from './WeekView2';
import SafeMarkdown from './SafeMarkdown';
import MobileSheet, { MOBILE_SHEET_ANIMATION_MS } from './calendar/MobileSheet';
import { themes } from '../config/themes';
import { CATEGORIES, catColor, categorizeEvent } from '../config/categories';
import { generateEventSlug, getLocalDateKey, isEventCanceled, parseEventDate } from '../utils/eventUtils';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { fs } from '../utils/typography';

const MOBILE_BREAKPOINT = 768; // matches Tailwind's md: breakpoint used throughout the app

function CalendarView({ curMonth, curYear, events, onModalStateChange, selectedEventSlug, onModalClose, isModalOpen, theme, onMonthChange, onYearChange, viewMode, onViewModeChange, currentWeek, selectedEvent: externalSelectedEvent, onEventSelect, onRightPaneChange, combinedEvents, isRightPaneOpen: externalIsRightPaneOpen, clearSelectedEventSlug, legendEvents, selectedCategory, selectedTags, onCategoryClick, onTagClick }) {
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [internalIsRightPaneOpen, setInternalIsRightPaneOpen] = useState(!!selectedEventSlug);
    const [starredState, setStarredState] = useState(false);
    const [isCrossMonthSwipeLoading, setIsCrossMonthSwipeLoading] = useState(false);
    const isSwipeMonthTransitionRef = useRef(false);

    const tokens = theme?.tokens || themes.light.tokens;

    // Mobile: the event detail becomes a bottom sheet (see MobileSheet) instead
    // of the in-flow panel desktop gets. isDetailClosing lets the sheet play its
    // slide-down-and-dismiss animation before selectedEvent actually clears, so
    // its content doesn't go blank mid-animation.
    const prefersReducedMotion = usePrefersReducedMotion();
    const [isMobileViewport, setIsMobileViewport] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
    );
    const [isDetailClosing, setIsDetailClosing] = useState(false);
    const closeTimeoutRef = useRef(null); // pending setSelectedEvent(null) from closeEventDetail's close animation
    // Rendered height of the open mobile sheet (0 when closed), reported by
    // MobileSheet. The day list gets this much extra bottom padding so even
    // its last rows have enough scroll room to clear the sheet.
    const [sheetHeight, setSheetHeight] = useState(0);
    const dayListRef = useRef(null); // the day list's own scroll container
    useEffect(() => {
        const handleResize = () => setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const normalizeDescription = (description) => {
        return typeof description === 'string' ? description : '';
    };

    const normalizeTags = (tags) => {
        if (Array.isArray(tags)) {
            return tags;
        }
        if (typeof tags === 'string') {
            return tags.split(',').map(tag => ({ name: tag.trim() })).filter(tag => tag.name);
        }
        return [];
    };

    const safeText = (value, fallback = '') => {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }
        return fallback;
    };

    useEffect(() => {
        if (selectedEvent) {
            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
            setStarredState(starred.includes(generateEventSlug(selectedEvent)));
        } else {
            setStarredState(false);
        }
    }, [selectedEvent]);

    // Use external state if provided, otherwise use internal state
    const isRightPaneOpen = externalIsRightPaneOpen !== undefined ? externalIsRightPaneOpen : internalIsRightPaneOpen;
    const setIsRightPaneOpen = onRightPaneChange || setInternalIsRightPaneOpen;

    // Reset sidebar when month/year changes
    useEffect(() => {
        if (isSwipeMonthTransitionRef.current) {
            isSwipeMonthTransitionRef.current = false;
            return;
        }

        setSelectedDay(null);
        setSelectedEvent(null);

        // if month/year is current month/year, set selected day to today
        if (curMonth === new Date().getMonth() + 1 && curYear === new Date().getFullYear()) {
            setSelectedDay(new Date());
        }

    }, [curMonth, curYear]);

    // Handle external event selection (from slug navigation)
    useEffect(() => {
        if (externalSelectedEvent) {
            const eventDate = parseEventDate(externalSelectedEvent.startDate) || new Date();
            setSelectedDay(eventDate);
            setSelectedEvent(externalSelectedEvent);
            setIsRightPaneOpen(true);
            
            // Update URL hash with event slug
            //const eventSlug = generateEventSlug(externalSelectedEvent);
            //if (window.location.hash !== `#${eventSlug}`) {
            //    window.history.pushState({}, '', `#${eventSlug}`);
            //}
        }
    }, [externalSelectedEvent]);

    // Ensure right pane is open when we have a selectedEventSlug
    useEffect(() => {
        if (selectedEventSlug && !isRightPaneOpen) {
            //console.log('Forcing right pane open for selectedEventSlug');
            setIsRightPaneOpen(true);
        }
    }, [selectedEventSlug, isRightPaneOpen]);

    // Stop showing the tiny right-pane loader once the selected day's month is loaded.
    useEffect(() => {
        if (!isCrossMonthSwipeLoading || !selectedDay) return;

        const selectedMonth = selectedDay.getMonth() + 1;
        const selectedYear = selectedDay.getFullYear();
        if (curMonth === selectedMonth && curYear === selectedYear) {
            setIsCrossMonthSwipeLoading(false);
        }
    }, [isCrossMonthSwipeLoading, selectedDay, curMonth, curYear]);

    // Handle selectedEventSlug navigation
    useEffect(() => {
        if (selectedEventSlug && events && events.length > 0) {
            //console.log('Processing selectedEventSlug in CalendarView:', selectedEventSlug);
            // Find the event by slug - check both regular events and combined events
            let targetEvent = events.find(event => {
                const eventSlug = generateEventSlug(event);
                return eventSlug === selectedEventSlug;
            });

            // If not found in regular events and we have combined events (week view), check there too
            if (!targetEvent && combinedEvents && combinedEvents.length > 0) {
                targetEvent = combinedEvents.find(event => {
                    const eventSlug = generateEventSlug(event);
                    return eventSlug === selectedEventSlug;
                });
            }

            if (targetEvent) {
                //console.log('Found target event in CalendarView:', targetEvent.title);
                // Set the selected day to the event's date
                const eventDate = parseEventDate(targetEvent.startDate) || new Date();
                setSelectedDay(eventDate);
                setSelectedEvent(targetEvent);
                setIsRightPaneOpen(true);
                
                // Update URL hash with event slug (in case it wasn't already set)
                const eventSlug = generateEventSlug(targetEvent);
                if (window.location.hash !== `#${eventSlug}`) {
                    window.history.pushState({}, '', `#${eventSlug}`);
                }
                
                // Call external callback if provided
                if (onEventSelect) {
                    onEventSelect(targetEvent);
                }
            } else {
                //console.log('Event not found in CalendarView for slug:', selectedEventSlug);
            }
        }
    }, [selectedEventSlug, events, combinedEvents, onEventSelect]);

    // Notify parent when right pane state changes
    useEffect(() => {
        if (onRightPaneChange) {
            onRightPaneChange(isRightPaneOpen);
        }        

    }, [isRightPaneOpen, onRightPaneChange]);

    // Clear selections when external right pane is closed
    useEffect(() => {
        if (externalIsRightPaneOpen !== undefined && !externalIsRightPaneOpen) {
            setSelectedDay(null);
            setSelectedEvent(null);
            
        }
    }, [externalIsRightPaneOpen]);

    // Get the current week's dates when in week view
    const getCurrentWeekDates = () => {
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            weekDates.push(date);
        }
        return weekDates;
    };

    // Restricts an event list to the currently displayed week's dates.
    const filterToCurrentWeek = (eventList) => {
        const weekDates = currentWeek ? (() => {
            const dates = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(currentWeek);
                date.setDate(currentWeek.getDate() + i);
                dates.push(date);
            }
            return dates;
        })() : (selectedWeek || getCurrentWeekDates());
        const weekDateStrs = weekDates.map(d => getLocalDateKey(d));
        return eventList.filter(e => weekDateStrs.includes(getLocalDateKey(e.startDate)));
    };

    // Get events for the current week
    const getCurrentWeekEvents = () => filterToCurrentWeek(events);

    // Same, but from the pre-category/tag event set — used for the week
    // legend's category/tag counts so they aren't self-narrowed by the
    // filters they control.
    const getCurrentWeekLegendEvents = () => filterToCurrentWeek(legendEvents || []);

    // Get events for a specific day
    const getEventsForDay = (day) => {
        if (!day) return [];
        const dayStr = getLocalDateKey(day);
        return events.filter(event => getLocalDateKey(event.startDate) === dayStr);
    };
   

    // Handle day selection
    const handleDaySelect = (day) => {
        // Get events for the selected day
        const dayEvents = getEventsForDay(day);
        
        // On small screens, only allow clicking on days that have events
        if (window.innerWidth < 768 && dayEvents.length === 0) {
            return; // Don't do anything if no events on small screens
        }
        
        setSelectedDay(day);
        setIsRightPaneOpen(true);
        
        // If there's exactly one event, automatically select it
        if (dayEvents.length === 1) {
            const singleEvent = dayEvents[0];
            setSelectedEvent(singleEvent);
            // Update URL hash with event slug
            const eventSlug = generateEventSlug(singleEvent);
            if (window.location.hash !== `#${eventSlug}`) {
                window.history.pushState({}, '', `#${eventSlug}`);
            }
            // Call external callback if provided
            if (onEventSelect) {
                onEventSelect(singleEvent);
            }
        } else {
            setSelectedEvent(null);
            // Clear URL hash when no specific event is selected
            window.history.pushState({}, '', window.location.pathname);
        }
    };

    // Handle event selection
    const handleEventSelect = (event, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        //console.log('handleEventSelect', event);

        // If this is a different event than the one from the slug, clear the slug to allow normal navigation
        if (selectedEventSlug) {
            const currentEventSlug = generateEventSlug(event);
            if (currentEventSlug !== selectedEventSlug) {
                //console.log('User selected different event, clearing selectedEventSlug for normal navigation');
                // Clear the slug to allow normal navigation
                // We'll update the URL hash below, but clear the slug state
                if (clearSelectedEventSlug) {
                    clearSelectedEventSlug();
                }
            }
        }

        //console.log('handleEventSelect: selectedEventSlug', selectedEventSlug);
        
        // Set the selected day to the event's date
        const eventDate = parseEventDate(event.startDate) || new Date();
        setSelectedDay(eventDate);
        setSelectedEvent(event);
        setIsRightPaneOpen(true);
        
        // Update URL hash with event slug
        const eventSlug = generateEventSlug(event);
        if (window.location.hash !== `#${eventSlug}`) {
            window.history.pushState({}, '', `#${eventSlug}`);
        }
        
        // Call external callback if provided
        if (onEventSelect) {
            onEventSelect(event);
        }
    };

    const scrollToEvent = (event) => {
        const eventElement = document.getElementById(`event-${generateEventSlug(event)}`);
        const container = dayListRef.current;
        if (!eventElement || !container || !container.contains(eventElement)) return;
        // Scroll ONLY the day list's own container — never scrollIntoView.
        // scrollIntoView walks the whole ancestor chain and will happily shift
        // any ancestor it finds scrollable (including <body> when layout
        // overflow makes it technically scrollable), which is how the entire
        // app shell used to get nudged up out of view with no way back.
        //
        // On mobile the detail is a bottom sheet overlaying this list from the
        // bottom (see MobileSheet), so pin the row to the top of the list,
        // which stays clear of the sheet regardless of its height; the list's
        // sheet-height bottom padding guarantees even the last row has the
        // scroll room to get there. Desktop keeps centering.
        //
        // Always instant (not 'smooth'): selecting a row re-renders every row
        // in the list (each one's `active` styling depends on selectedEvent),
        // and that layout churn can interrupt an in-flight smooth scroll,
        // silently leaving it wherever it happened to be. Reliably landing in
        // the right place matters more here than the animation.
        const rowRect = eventElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const delta = isMobileViewport
            ? rowRect.top - containerRect.top - 13 // pin to top, minus the list's own top padding
            : rowRect.top - containerRect.top - (container.clientHeight - rowRect.height) / 2; // center
        container.scrollTop += delta;
    };

    // Runs the scroll-into-view for whichever event just became selected,
    // regardless of how (tap, URL slug, single-event auto-select), deferred
    // to the next frame so it lands after the selection's own re-render
    // rather than racing it. Also cancels any pending close-animation timeout
    // (see closeEventDetail) and resets isDetailClosing — without this,
    // tapping a new event while a previous one's sheet was still animating
    // closed left isDetailClosing stuck true (so isOpen computed false and
    // the sheet silently failed to open for the new event), and the stale
    // timeout would then null out the brand-new selection a moment later.
    useEffect(() => {
        if (!selectedEvent) return undefined;
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsDetailClosing(false);
        // Double-RAF: the first frame lets the selection's own re-render (and
        // the list's sheet-height padding) actually commit and paint; scrolling
        // on the second frame measures the settled layout.
        let raf2 = null;
        const raf = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => scrollToEvent(selectedEvent));
        });
        // The layout can still settle *after* that scroll (e.g. a late-loading
        // display font growing the pane header, which shrinks this list) — and
        // when the scroll had clamped at the list's end, that settling strands
        // the row short of its target with nothing to re-trigger the scroll.
        // Watch the list's own box for ~a second after selection and re-assert
        // the scroll when it changes.
        let observer = null;
        let observerTimeout = null;
        if (dayListRef.current && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => scrollToEvent(selectedEvent));
            observer.observe(dayListRef.current);
            observerTimeout = setTimeout(() => observer.disconnect(), 1200);
        }
        return () => {
            cancelAnimationFrame(raf);
            if (raf2 !== null) cancelAnimationFrame(raf2);
            if (observer) observer.disconnect();
            if (observerTimeout) clearTimeout(observerTimeout);
        };
        // Deliberately keyed on selectedEvent alone: this effect also cancels
        // an in-flight close (see above), so it must NOT re-run for mere
        // sheet-height changes — the sheet reporting height 0 as it closes
        // would revive itself by resetting isDetailClosing mid-animation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEvent]);

    // Re-assert the scroll when the open sheet's measured height (and with it
    // the list's extra bottom padding) lands or changes — that happens a beat
    // after the selection renders, and rows near the end of the list need the
    // padding applied before they have the scroll room to reach the top.
    // Skipped while the sheet is closing: its height dropping to 0 must not
    // trigger a scroll (and the selection is about to clear anyway).
    useEffect(() => {
        if (!selectedEvent || isDetailClosing || sheetHeight <= 0) return undefined;
        const raf = requestAnimationFrame(() => scrollToEvent(selectedEvent));
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetHeight]);

    // Closes the event detail. On mobile (where it's a bottom sheet) this lets
    // the sheet slide down first — selectedEvent only actually clears once that
    // animation finishes, so the sheet's content stays valid throughout instead
    // of going blank mid-slide. Skipped for anyone who prefers reduced motion.
    const closeEventDetail = () => {
        window.history.pushState({}, '', window.location.pathname);
        if (isMobileViewport && !prefersReducedMotion) {
            setIsDetailClosing(true);
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = window.setTimeout(() => {
                setSelectedEvent(null);
                setIsDetailClosing(false);
                closeTimeoutRef.current = null;
            }, MOBILE_SHEET_ANIMATION_MS);
        } else {
            setSelectedEvent(null);
        }
    };

    // Handle pane state change
    const handlePaneStateChange = (isOpen) => {
        // Only prevent closing the pane if we have a selectedEventSlug and we're trying to close it
        // Allow opening the pane normally
        //console.log('handlePaneStateChange', isOpen);
        //if (!isOpen && selectedEventSlug) {
            //console.log('Preventing right pane close due to selectedEventSlug');
            //return;
        //}
        
        setIsRightPaneOpen(isOpen);

        if (isOpen === false) {
            // Clear selections when pane is closed
            setSelectedDay(null);
            setSelectedEvent(null);
            // Clear URL hash when closing pane
            window.history.pushState({}, '', window.location.pathname);
            // Clear the selectedEventSlug to allow normal navigation
            if (clearSelectedEventSlug) {
                clearSelectedEventSlug();
            }
        }
    };

    const handleLinkClick = (e, url) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleShare = (event) => {
        const slug = generateEventSlug(event);
        const url = `${window.location.origin}/#${slug}`;
        navigator.clipboard.writeText('Check out this event I found on ATLTech.events: ' + url).then(() => {
            // Show a toast notification
            if (window.showToast) {
                window.showToast('Event URL copied to clipboard!');
            }
        }).catch(err => {
            //console.error('Failed to copy URL:', err);
        });
    };

    const renderTextWithLinks = (text) => {
        if (!text) return null;
        
        // URL regex pattern
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        
        // Split text by URLs and map to elements
        const parts = text.split(urlPattern);
        return parts.map((part, index) => {
            if (part.match(urlPattern)) {
                return (
                    <a
                        key={index}
                        href={part}
                        onClick={(e) => handleLinkClick(e, part)}
                        className={`${theme.link} underline break-all`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {part}
                    </a>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const renderNavigationTitle = () => {
        if (viewMode === 'month') {
            const date = new Date(curYear, curMonth - 1, 1);
            return date.toLocaleString('default', { month: 'long', year: 'numeric' });
        } else {
            const startDate = selectedWeek?.[0] || getCurrentWeekDates()[0];
            const endDate = selectedWeek?.[6] || getCurrentWeekDates()[6];
            return `${startDate.toLocaleString('default', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
    };

    const leftContent = (
        <div className="flex flex-col h-full">
            {/* Calendar View */}
            <div className="flex-1 min-h-0">
                {viewMode === 'month' ? (
                    <MonthView
                        curMonth={curMonth}
                        curYear={curYear}
                        events={events}
                        theme={theme}
                        onDaySelect={handleDaySelect}
                        onEventSelect={handleEventSelect}
                        selectedDay={selectedDay}
                        legendEvents={legendEvents}
                        selectedCategory={selectedCategory}
                        selectedTags={selectedTags}
                        onCategoryClick={onCategoryClick}
                        onTagClick={onTagClick}
                    />
                ) : (
                    <WeekView2
                        days={currentWeek ? (() => {
                            const dates = [];
                            for (let i = 0; i < 7; i++) {
                                const date = new Date(currentWeek);
                                date.setDate(currentWeek.getDate() + i);
                                dates.push(date);
                            }
                            return dates;
                        })() : (selectedWeek || getCurrentWeekDates())}
                        events={getCurrentWeekEvents()}
                        theme={theme}
                        onDaySelect={handleDaySelect}
                        onEventSelect={handleEventSelect}
                        selectedDay={selectedDay}
                        legendEvents={getCurrentWeekLegendEvents()}
                        selectedCategory={selectedCategory}
                        selectedTags={selectedTags}
                        onCategoryClick={onCategoryClick}
                        onTagClick={onTagClick}
                    />
                )}
            </div>
        </div>
    );

    // Swipe left/right on the right pane navigates to the next/prev day on mobile
    const navigateDayByOffset = (offset) => {
        if (!selectedDay) return;
        const newDay = new Date(selectedDay);
        newDay.setDate(newDay.getDate() + offset);

        const nextMonth = newDay.getMonth() + 1;
        const nextYear = newDay.getFullYear();
        const monthChanged = nextMonth !== curMonth || nextYear !== curYear;

        if (monthChanged) {
            setIsCrossMonthSwipeLoading(true);
            isSwipeMonthTransitionRef.current = true;
            if (nextYear !== curYear && onYearChange) onYearChange(nextYear, { preserveRightPane: true });
            if ((nextMonth !== curMonth || nextYear !== curYear) && onMonthChange) onMonthChange(nextMonth, { preserveRightPane: true });
        } else {
            setIsCrossMonthSwipeLoading(false);
        }

        setSelectedDay(newDay);
        setSelectedEvent(null);
        window.history.pushState({}, '', window.location.pathname);
        // Auto-select if there's exactly one event on the new day
        const dayEvents = getEventsForDay(newDay);
        if (dayEvents.length === 1) {
            const singleEvent = dayEvents[0];
            setSelectedEvent(singleEvent);
            window.history.pushState({}, '', `#${generateEventSlug(singleEvent)}`);
            if (onEventSelect) onEventSelect(singleEvent);
        }
    };

    const rightPaneSwipeHandlers = useSwipeable({
        onSwipedLeft: () => { if (window.innerWidth < 768 && viewMode !== 'week') navigateDayByOffset(1); },
        onSwipedRight: () => { if (window.innerWidth < 768 && viewMode !== 'week') navigateDayByOffset(-1); },
        preventScrollOnSwipe: false,
        trackMouse: false,
    });

    const tc = tokens.colors;
    const selectedCat = selectedEvent ? categorizeEvent(selectedEvent) : null;
    const selectedCatColors = selectedCat ? catColor(selectedCat, tokens) : null;

    const formatDetailTime = (event) => {
        const start = parseEventDate(event.startDate);
        const end = parseEventDate(event.endDate || event.startDate);
        if (!start) return 'All day';
        if (start.getHours() === 0 && start.getMinutes() === 0) return 'All day';
        const opts = { hour: 'numeric', minute: '2-digit' };
        return end && end.getTime() !== start.getTime()
            ? `${start.toLocaleTimeString([], opts)} – ${end.toLocaleTimeString([], opts)}`
            : start.toLocaleTimeString([], opts);
    };

    // Expanded event detail content — shared between the desktop in-flow panel
    // and the mobile bottom sheet (see rightContent below). Returns null when
    // there's nothing selected.
    const renderEventDetail = () => selectedEvent && (
        <>
            <div className="flex items-start justify-between gap-2.5">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span style={{
                        fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600,
                        color: selectedCatColors.fillText, background: selectedCatColors.fillBg, padding: '3px 7px', borderRadius: 5,
                    }}>
                        <i className={`fas ${CATEGORIES[selectedCat].icon}`} style={{ marginRight: 4, fontSize: fs(8) }} aria-hidden="true" />
                        {CATEGORIES[selectedCat].label}
                    </span>
                    {isEventCanceled(selectedEvent) && (
                        <span style={{
                            fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600,
                            color: tc.muted, border: `1px solid ${tc.gridLine}`, padding: '2px 7px', borderRadius: 5,
                        }} aria-label="Canceled event"><i className="fas fa-ban mr-1" aria-hidden="true"></i>Canceled</span>
                    )}
                    {!isEventCanceled(selectedEvent) && selectedEvent.eventType === 'Featured' && (
                        <span style={{
                            fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600,
                            color: tc.accent, border: `1px solid ${tc.accent}`, padding: '2px 7px', borderRadius: 5,
                        }} aria-label="Featured event"><i className="fas fa-star mr-1" aria-hidden="true"></i>Featured</span>
                    )}
                </div>
                <div className="flex gap-1.5 flex-none">
                    <button
                        onClick={() => {
                            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
                            const eventSlug = generateEventSlug(selectedEvent);
                            const newStarred = starred.includes(eventSlug)
                                ? starred.filter(slug => slug !== eventSlug)
                                : [...starred, eventSlug];
                            localStorage.setItem('starredEvents', JSON.stringify(newStarred));
                            setStarredState(newStarred.includes(eventSlug));
                        }}
                        className="flex items-center justify-center"
                        style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${tc.gridLine}`, color: tc.accent, background: 'transparent' }}
                        aria-label={starredState ? 'Unfavorite this event' : 'Favorite this event'}
                        title={starredState ? 'Unfavorite' : 'Favorite'}
                    >
                        <i className={`${starredState ? 'fas' : 'far'} fa-heart text-xs`} aria-hidden="true"></i>
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeEventDetail();
                        }}
                        className="flex items-center justify-center"
                        style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${tc.gridLine}`, color: tc.muted, background: 'transparent' }}
                        aria-label="Close event details"
                        title="Close event details"
                    >
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" /></svg>
                    </button>
                </div>
            </div>

            <h3 style={{
                fontFamily: tokens.fonts.display, fontSize: fs(19), fontWeight: 600, color: tc.ink, lineHeight: 1.2, marginTop: 11,
                textDecoration: isEventCanceled(selectedEvent) ? 'line-through' : 'none',
            }}>
                {safeText(selectedEvent.title, 'Event Details')}
            </h3>
            <div style={{ fontSize: fs(12.5), color: tc.muted, marginTop: 5 }}>
                Hosted by <span style={{ color: tc.ink, fontWeight: 600 }}>{safeText(selectedEvent.organization, 'Unknown organization')}</span>
            </div>

            {selectedEvent.description && (
                <div style={{ fontSize: fs(12.5), lineHeight: 1.55, color: tc.muted, marginTop: 13 }}>
                    <SafeMarkdown text={normalizeDescription(selectedEvent.description)} theme={theme} />
                </div>
            )}

            <div className="flex flex-col gap-2" style={{ marginTop: 15 }}>
                <div className="flex items-start gap-2.5">
                    <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase', color: tc.faint, fontWeight: 600, width: 58, flex: '0 0 auto', paddingTop: 1 }}>Time</span>
                    <span style={{ fontSize: fs(12.5), color: tc.ink, fontWeight: 500, lineHeight: 1.4 }}>{formatDetailTime(selectedEvent)}</span>
                </div>
                {selectedEvent.location && (
                    <div className="flex items-start gap-2.5">
                        <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase', color: tc.faint, fontWeight: 600, width: 58, flex: '0 0 auto', paddingTop: 1 }}>Location</span>
                        <span style={{ fontSize: fs(12.5), color: tc.ink, fontWeight: 500, lineHeight: 1.4 }}>{safeText(selectedEvent.location)}</span>
                    </div>
                )}
            </div>

            {normalizeTags(selectedEvent.tags).length > 0 && (
                <div className="flex flex-wrap gap-1.5" style={{ marginTop: 13 }}>
                    {normalizeTags(selectedEvent.tags).map((tag, index) => (
                        <span key={index} style={{
                            fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase',
                            fontWeight: 600, color: tc.muted, border: `1px solid ${tc.gridLine}`, padding: '3px 7px', borderRadius: 5,
                        }}>
                            {typeof tag === 'string' ? tag : tag?.name || ''}
                        </span>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-2.5" style={{ marginTop: 17 }}>
                {isEventCanceled(selectedEvent) ? (
                    <span
                        className="flex-1 text-center"
                        style={{
                            padding: '10px 0', borderRadius: 9, border: `1px solid ${tc.gridLine}`, color: tc.faint,
                            fontSize: fs(13), fontWeight: 700,
                        }}
                        aria-label="This event has been canceled"
                    >
                        <i className="fas fa-ban mr-2" aria-hidden="true"></i>Event Canceled
                    </span>
                ) : selectedEvent.eventURL && (() => {
                    const eventEnd = parseEventDate(selectedEvent.endDate || selectedEvent.startDate);
                    if (!eventEnd) return false;
                    return eventEnd >= new Date(new Date().setHours(0, 0, 0, 0));
                })() && (
                    <a
                        href={selectedEvent.eventURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center"
                        style={{
                            padding: '10px 0', borderRadius: 9, background: tc.accent, color: tc.todayInk,
                            fontSize: fs(13), fontWeight: 700,
                        }}
                    >
                        {selectedEvent.eventURL === '/private-events' ? 'About private events' : 'Register'}
                    </a>
                )}
                <button
                    onClick={() => handleShare(selectedEvent)}
                    style={{
                        padding: '10px 14px', borderRadius: 9, border: `1px solid ${tc.gridLine}`, color: tc.muted,
                        fontSize: fs(13), fontWeight: 600, background: 'transparent',
                    }}
                    title="Copy event link to clipboard"
                >
                    <i className="fas fa-share-alt mr-1.5" aria-hidden="true"></i>Share
                </button>
            </div>

            {selectedEvent.sponsorLogo && (
                <div className="text-center" style={{ marginTop: 14 }}>
                    <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase', color: tc.faint, fontWeight: 600 }}>Event Hosted By</span>
                    <a href={selectedEvent.sponsorURL} target="_blank" rel="noopener noreferrer" className="block mt-1">
                        <img src={selectedEvent.sponsorLogo} alt={selectedEvent.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                    </a>
                </div>
            )}
        </>
    );

    // Close button per the design Sidebar spec — clears selections + hash via handlePaneStateChange
    const closeButton = (
        <button
            onClick={() => handlePaneStateChange(false)}
            className="flex-none flex items-center justify-center"
            style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${tc.gridLine}`, background: tc.surface, color: tc.muted, marginTop: 2 }}
            aria-label="Close details panel"
            title="Close panel"
        >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" /></svg>
        </button>
    );

    const rightContent = (
        <div
            data-day-swipe-zone="true"
            {...rightPaneSwipeHandlers}
            className="h-full flex flex-col overflow-hidden"
            style={{ background: tc.surfaceAlt, color: tc.ink, fontFamily: tokens.fonts.ui, textAlign: 'left', borderLeft: `1px solid ${tc.gridLine}` }}
        >
            {/* Date header + close */}
            <div className="flex-none flex items-start justify-between gap-3" style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${tc.gridLine}` }}>
                <div className="min-w-0">
                    {selectedDay ? (
                        <>
                            <div style={{
                                fontFamily: tokens.fonts.mono, fontSize: fs(10.5), letterSpacing: 1,
                                textTransform: 'uppercase', color: tc.accent, fontWeight: 600,
                            }}>
                                {parseEventDate(selectedDay)?.toLocaleDateString('en-US', { weekday: 'long' }) || 'Selected day'}
                            </div>
                            <h2 style={{ fontFamily: tokens.fonts.display, fontSize: fs(25), fontWeight: 500, marginTop: 1, lineHeight: 1.15 }}>
                                {parseEventDate(selectedDay)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </h2>
                        </>
                    ) : (
                        <h2 style={{ fontFamily: tokens.fonts.display, fontSize: fs(21), fontWeight: 500, color: tc.muted, marginTop: 3 }}>
                            Event details
                        </h2>
                    )}
                </div>
                {closeButton}
            </div>

            {selectedDay ? (
                <div className="flex-1 flex flex-col min-h-0 relative">
                    {/* Day's events */}
                    <div
                        ref={dayListRef}
                        className={`overflow-y-auto min-h-0 ${(!isMobileViewport && selectedEvent) ? 'max-h-[42%]' : 'flex-1'}`}
                        style={{
                            padding: '13px 16px',
                            // While the mobile sheet is open it overlays the bottom of
                            // this list; matching bottom padding lets every row —
                            // including the last few — scroll up clear of it.
                            paddingBottom: (isMobileViewport && sheetHeight > 0) ? sheetHeight + 13 : 13,
                        }}
                    >
                        {isCrossMonthSwipeLoading && (
                            <div className="flex items-center gap-2 mb-3" style={{ fontSize: fs(12), color: tc.muted }} role="status" aria-live="polite">
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                                <span>Loading next month...</span>
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            {getEventsForDay(selectedDay).map(event => {
                                const evCat = categorizeEvent(event);
                                const ec = catColor(evCat, tokens);
                                const active = selectedEvent?.id === event.id;
                                const evCanceled = isEventCanceled(event);
                                return (
                                    <div
                                        key={event.id}
                                        id={`event-${generateEventSlug(event)}`}
                                        className="cursor-pointer"
                                        style={{
                                            padding: '10px 12px', borderRadius: 10,
                                            background: active ? tc.surface : 'transparent',
                                            border: `1px solid ${active ? ec.strong : tc.gridLine}`,
                                            boxShadow: active ? `0 2px 10px ${tokens.mode === 'dark' ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.06)'}` : 'none',
                                            opacity: evCanceled ? 0.6 : 1,
                                        }}
                                        onClick={(e) => handleEventSelect(event, e)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleEventSelect(event, e);
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                                            <i className={`fas ${CATEGORIES[evCat].icon}`} style={{ color: ec.strong, fontSize: fs(9), flex: '0 0 auto', width: 10, textAlign: 'center' }} aria-hidden="true" />
                                            <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(10.5), color: tc.muted, fontWeight: 500 }}>
                                                {formatDetailTime(event)}
                                            </span>
                                            {evCanceled && (
                                                <span style={{
                                                    fontFamily: tokens.fonts.mono, fontSize: fs(9), letterSpacing: 0.4, textTransform: 'uppercase',
                                                    fontWeight: 700, color: tc.muted, border: `1px solid ${tc.gridLine}`, borderRadius: 5, padding: '1px 5px',
                                                }}>Canceled</span>
                                            )}
                                            {!evCanceled && event.eventType === 'Featured' && (
                                                <i className="fas fa-star" style={{ color: ec.strong, fontSize: fs(10) }} aria-label="Featured event" />
                                            )}
                                        </div>
                                        <div style={{ fontSize: fs(13.5), fontWeight: 600, color: tc.ink, lineHeight: 1.3, textDecoration: evCanceled ? 'line-through' : 'none' }}>{safeText(event.title, 'Event')}</div>
                                        <div style={{ fontSize: fs(11.5), color: tc.muted, marginTop: 2 }}>{safeText(event.organization, 'Unknown organization')}</div>
                                    </div>
                                );
                            })}
                            {getEventsForDay(selectedDay).length === 0 && (
                                <div className="text-center py-8" style={{ color: tc.muted }}>
                                    No events scheduled for this day
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Expanded detail — strong editorial rule + surface shift to separate it from the day list.
                        Desktop keeps it in-flow below the list; mobile turns it into a slide-up bottom sheet
                        (see MobileSheet) since there's no room to show both at once. */}
                    {selectedEvent && !isMobileViewport && (
                        <div className="flex-1 min-h-0 overflow-y-auto" style={{
                            borderTop: `2px solid ${tc.ink}`,
                            background: tc.surface,
                            boxShadow: `inset 0 6px 8px -6px ${tokens.mode === 'dark' ? 'rgba(0,0,0,.55)' : 'rgba(30,36,48,.18)'}`,
                            padding: '14px 20px 18px',
                        }}>
{renderEventDetail()}
                        </div>
                    )}
                    {isMobileViewport && (
                        <MobileSheet
                            isOpen={!!selectedEvent && !isDetailClosing}
                            onDismiss={closeEventDetail}
                            tokens={tokens}
                            ariaLabel="Close event details (drag down or press)"
                            onHeightChange={setSheetHeight}
                        >
                            <div style={{ padding: '14px 20px 18px' }}>
                                {renderEventDetail()}
                            </div>
                        </MobileSheet>
                    )}
                </div>
            ) : (
                // Default view when no day is selected
                <div className="flex-1 flex items-center justify-center" style={{ color: tc.muted }}>
                    <div className="text-center">
                        <i className="fas fa-calendar-alt text-4xl mb-4" style={{ color: tc.faint }}></i>
                        <p>Select a day to view its events</p>
                        <p className="text-sm mt-2" style={{ color: tc.faint }}>Click an event to see its details</p>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`h-full ${theme.background} ${theme.text}`}>
            <SplitPane
                leftContent={leftContent}
                rightContent={rightContent}
                defaultRightPaneWidth={370}
                isRightPaneOpen={isRightPaneOpen}
                onPaneStateChange={handlePaneStateChange}
            />
        </div>
    );
}

export default CalendarView; 